import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {css as pandaCss} from "@panda/css";

import {mb, mt, p, pb} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {leadButtonCopyPlural} from "../../../../lead/constants/lead_button_copy_plural";
import {MessageType} from "../../../types/MessageType";
import {ApplicationSource} from "../../../utils/ApplicationSource";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {SelectRooms} from "../fields/SelectRooms";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {MessageAndQuestionsSection} from "./MessageAndQuestionsSection";
import {PropertyInfo} from "./PropertyInfo";
import {SubmitButton} from "./SubmitButton";
import {VendorDisplay} from "./VendorDisplay";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton?: boolean;
}

export const ApplicationDefaultForm = (props: IProps) => {
    const {
        offer,
        property,
        vendor,
        hideRooms,
        hideSubmitButton,
        source,
        onCloseModal,
        leadIsSubmitting,
        applicationMessageLogic: {
            currentState: {messageType}
        }
    } = useApplicationFormContext();
    const {isMobile} = useUserDevice();

    const leadBtnText = property ? askForPriceleadButtonCopy : leadButtonCopyPlural;

    return (
        <div css={formWrapperStyle} data-testid={OFFER_TEST_IDS.LEAD_FORM.OFFER_LEAD_FORM}>
            <div css={leftSideStyle}>
                <div css={vendorHeaderStyle}>
                    <VendorDisplay offer={offer} onCloseModal={onCloseModal} vendor={vendor} css={vendorDisplayStyle} />
                </div>
                <PropertyInfo css={mb(3)} />
                {!hideRooms && <SelectRooms label="Liczba pokoi, która mnie interesuje" css={mb(2)} />}
                <YourData
                    showEmailField={props.showEmailField}
                    showPhoneField={props.showPhoneField}
                    showNameField={props.showNameField}
                    onShowAllInputFieldsClick={props.showAllInputFields}
                    css={yourDataStyle}
                />
                {!isMobile && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />
                        {vendor && (
                            <LeadVendorRodoTerms
                                vendor={vendor}
                                buttonText={props.showRevealPriceButton ? leadBtnText : "Wyślij"}
                                offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>
            <RightSideWrapper>
                {messageType !== MessageType.NONE && <MessageAndQuestionsSection showMessageOnly={props.showTextAreaOnly} messageType={messageType} />}

                <ApplicationOptIns showMktEmailField={props.showMktEmailField} showMktSmsField={props.showMktSmsField} css={mb(2)} />
                <div>
                    {!hideSubmitButton &&
                        (props.showRevealPriceButton ? (
                            <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={leadBtnText} />
                        ) : (
                            <SubmitButton
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                css={mt(2)}
                                type="submit"
                                variant="filled_primary"
                                disabled={leadIsSubmitting}
                            >
                                {leadIsSubmitting ? <Loader /> : <>Wyślij</>}
                            </SubmitButton>
                        ))}

                    {source === ApplicationSource.RevealedPrices && (
                        <Text variant="body_copy_1">
                            Wypełnij formularz, żeby poznać cenę tego mieszkania. Deweloper może skontaktować się z Tobą, żeby przedstawić Ci szczegóły oferty.
                        </Text>
                    )}
                </div>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={props.showRevealPriceButton ? leadBtnText : "Wyślij"} />
                    {isMobile && (
                        <>
                            <LeadRodoTerms />
                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={leadBtnText}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </RightSideWrapper>
        </div>
    );
};

//  Styles
const formWrapperStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
    }
`;

const leftSideStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    ${p(2, 2, 0, 2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2)};
        width: 35.4rem;
    }
`;

const vendorHeaderStyle = css`
    ${pb(3)};
    display: flex;
`;

const vendorDisplayStyle = css`
    flex: 1 1 auto;
`;

export const yourDataStyle = (theme: Theme) => css`
    margin: 0 0 1.2rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.6rem 0;
    }
`;

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: "0 0 auto"
});

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const RightSideWrapper = styled.div`
    ${p(2)};
    ${({theme}) => css`
        @media (min-width: ${theme.breakpoints.md}) {
            background-color: ${theme.colors.gray[100]};
            width: 45.6rem;
        }
    `}
`;
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {css as pandaCss} from "@panda/css";

import {mb, mt, p, pb} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {LeadButtonTerms} from "../../../../lead/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../../lead/components/LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../lead/components/LeadVendorRodoTerms";
import {askForPriceleadButtonCopy} from "../../../../lead/constants/lead_button_copy";
import {leadButtonCopyPlural} from "../../../../lead/constants/lead_button_copy_plural";
import {MessageType} from "../../../types/MessageType";
import {ApplicationSource} from "../../../utils/ApplicationSource";
import {useApplicationFormContext} from "../ApplicationFormContext";
import {ApplicationOptIns} from "../fields/ApplicationOptIns";
import {SelectRooms} from "../fields/SelectRooms";
import {YourData} from "../fields/YourData";
import {ApplicationRevealButtonWithWrapper} from "./ApplicationRevealButtonWithWrapper";
import {MessageAndQuestionsSection} from "./MessageAndQuestionsSection";
import {PropertyInfo} from "./PropertyInfo";
import {SubmitButton} from "./SubmitButton";
import {VendorDisplay} from "./VendorDisplay";

interface IProps {
    showEmailField: boolean;
    showPhoneField: boolean;
    showNameField: boolean;
    showAllInputFields: () => void;
    showTextAreaOnly: boolean;
    showMktEmailField: boolean;
    showMktSmsField: boolean;
    showRevealPriceButton?: boolean;
}

export const ApplicationDefaultForm = (props: IProps) => {
    const {
        offer,
        property,
        vendor,
        hideRooms,
        hideSubmitButton,
        source,
        onCloseModal,
        leadIsSubmitting,
        applicationMessageLogic: {
            currentState: {messageType}
        }
    } = useApplicationFormContext();
    const {isMobile} = useUserDevice();

    const leadBtnText = property ? askForPriceleadButtonCopy : leadButtonCopyPlural;

    return (
        <div css={formWrapperStyle} data-testid={OFFER_TEST_IDS.LEAD_FORM.OFFER_LEAD_FORM}>
            <div css={leftSideStyle}>
                <div css={vendorHeaderStyle}>
                    <VendorDisplay offer={offer} onCloseModal={onCloseModal} vendor={vendor} css={vendorDisplayStyle} />
                </div>
                <PropertyInfo css={mb(3)} />
                {!hideRooms && <SelectRooms label="Liczba pokoi, która mnie interesuje" css={mb(2)} />}
                <YourData
                    showEmailField={props.showEmailField}
                    showPhoneField={props.showPhoneField}
                    showNameField={props.showNameField}
                    onShowAllInputFieldsClick={props.showAllInputFields}
                    css={yourDataStyle}
                />
                {!isMobile && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />
                        {vendor && (
                            <LeadVendorRodoTerms
                                vendor={vendor}
                                buttonText={props.showRevealPriceButton ? leadBtnText : "Wyślij"}
                                offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>
            <RightSideWrapper>
                {messageType !== MessageType.NONE && <MessageAndQuestionsSection showMessageOnly={props.showTextAreaOnly} messageType={messageType} />}

                <ApplicationOptIns showMktEmailField={props.showMktEmailField} showMktSmsField={props.showMktSmsField} css={mb(2)} />
                <div>
                    {!hideSubmitButton &&
                        (props.showRevealPriceButton ? (
                            <ApplicationRevealButtonWithWrapper leadIsSubmitting={leadIsSubmitting} leadBtnText={leadBtnText} />
                        ) : (
                            <SubmitButton
                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                css={mt(2)}
                                type="submit"
                                variant="filled_primary"
                                disabled={leadIsSubmitting}
                            >
                                {leadIsSubmitting ? <Loader /> : <>Wyślij</>}
                            </SubmitButton>
                        ))}

                    {source === ApplicationSource.RevealedPrices && (
                        <Text variant="body_copy_1">
                            Wypełnij formularz, żeby poznać cenę tego mieszkania. Deweloper może skontaktować się z Tobą, żeby przedstawić Ci szczegóły oferty.
                        </Text>
                    )}
                </div>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={props.showRevealPriceButton ? leadBtnText : "Wyślij"} />
                    {isMobile && (
                        <>
                            <LeadRodoTerms />
                            {vendor && (
                                <LeadVendorRodoTerms
                                    vendor={vendor}
                                    buttonText={leadBtnText}
                                    offerPrivacyPolicyUrl={offer?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offer?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offer?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </RightSideWrapper>
        </div>
    );
};

//  Styles
const formWrapperStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
    }
`;

const leftSideStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    ${p(2, 2, 0, 2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2)};
        width: 35.4rem;
    }
`;

const vendorHeaderStyle = css`
    ${pb(3)};
    display: flex;
`;

const vendorDisplayStyle = css`
    flex: 1 1 auto;
`;

export const yourDataStyle = (theme: Theme) => css`
    margin: 0 0 1.2rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 2.6rem 0;
    }
`;

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: "0 0 auto"
});

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2
});

const RightSideWrapper = styled.div`
    ${p(2)};
    ${({theme}) => css`
        @media (min-width: ${theme.breakpoints.md}) {
            background-color: ${theme.colors.gray[100]};
            width: 45.6rem;
        }
    `}
`;
